/**
 * Whether this tablet is locked, and after how long it locks.
 *
 * Both are kept on the device rather than in memory: the lock so that a reload
 * does not open it, the delay because it is a fact about where the tablet
 * stands. One store for both, so the lock screen and the terminal sheet read
 * the same answer. See `lock.tsx` for why any of it exists.
 */

import { useSyncExternalStore } from 'react';

const LOCKED_KEY = 'pos.lock.on';
const MINUTES_KEY = 'pos.lock.minutes';

/** Off, or how many minutes of nobody touching the screen. */
export const LOCK_CHOICES = [0, 1, 2, 5, 10] as const;
export type LockMinutes = (typeof LOCK_CHOICES)[number];
const DEFAULT_MINUTES: LockMinutes = 2;

const listeners = new Set<() => void>();

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage refused: the lock still works for this page, it just forgets.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

function storedMinutes(): LockMinutes {
  const raw = Number(read(MINUTES_KEY));
  return (LOCK_CHOICES as readonly number[]).includes(raw) && read(MINUTES_KEY) !== null
    ? (raw as LockMinutes)
    : DEFAULT_MINUTES;
}

/** The delay this tablet locks after, and a way to change it. */
export function useLockMinutes(): [LockMinutes, (next: LockMinutes) => void] {
  const minutes = useSyncExternalStore(subscribe, storedMinutes);
  return [minutes, (next) => write(MINUTES_KEY, String(next))];
}

export function useLocked(): boolean {
  return useSyncExternalStore(subscribe, () => read(LOCKED_KEY) === '1');
}

export const setLocked = (on: boolean) => write(LOCKED_KEY, on ? '1' : null);
